import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import platformAPI from "../../../services/platformApi";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  MEMBERSHIPS_LANDING_PAGE_TITLE,
  MEMBERSHIPS_LANDING_PAGE_DESCRIPTION,
  MEMBERSHIPS_LANDING_PAGE_KEYWORDS,
  MEMBERSHIPS_LANDING_PATH,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  LANDING_PRICE_FROM,
  buildMembershipsLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import Preloader from "../../common/Preloader";
import Seo from "../../Seo";
import s from "../CleanAdLanding/cleanAdLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

function ArtMembership() {
  return (
    <div className={`${s.art} ${s.artMembership}`} aria-hidden>
      <div className={s.artTicket}>
        <span>Абонемент</span>
        <strong>12 / 16</strong>
      </div>
      <div className={s.artCoin} />
    </div>
  );
}

function ArtDebtors() {
  return (
    <div className={`${s.art} ${s.artParents}`} aria-hidden>
      <div className={s.artCardMini}>
        <strong>3</strong>
        <span>должника</span>
      </div>
      <div className={s.artCardMini}>
        <strong>₽</strong>
        <span>к оплате</span>
      </div>
    </div>
  );
}

function ArtRevenue() {
  return (
    <div className={`${s.art} ${s.artSchedule}`} aria-hidden>
      <div className={s.artCal}>
        {Array.from({ length: 9 }, (_, i) => (
          <i key={i} className={i === 2 || i === 5 || i === 8 ? s.artCalOn : undefined} />
        ))}
      </div>
      <div className={s.artBadge}>+48к</div>
    </div>
  );
}

function ArtAcquiring() {
  return (
    <div className={`${s.art} ${s.artJournal}`} aria-hidden>
      <div className={s.artWindow}>
        <div className={s.artDots}>
          <i />
          <i />
          <i />
        </div>
        <div className={s.artRows}>
          <span className={s.artRowOk} />
          <span className={s.artRowOk} />
          <span className={s.artRowOk} />
          <span className={s.artRowMiss} />
        </div>
      </div>
      <div className={s.artToggle}>
        <b />
      </div>
    </div>
  );
}

const BENEFITS = [
  {
    title: "Типы абонементов",
    text: "По числу занятий или по сроку. Заморозка и продление — без отдельной таблицы.",
    icon: "01",
  },
  {
    title: "Должники сразу видны",
    text: "Кто не оплатил секцию — в одном списке. Не нужно сверять чаты и Excel.",
    icon: "02",
  },
  {
    title: "Оплата картой",
    text: "Родитель платит в кабинете. Деньги идут на ваши ключи эквайринга.",
    icon: "03",
  },
];

const CAPABILITIES = [
  {
    id: "types",
    title: "Типы абонементов",
    bullets: [
      "Пакет занятий или период",
      "Остаток после каждой отметки",
      "Видно, у кого пакет заканчивается",
    ],
    Art: ArtMembership,
  },
  {
    id: "debtors",
    title: "Должники",
    bullets: [
      "Список тех, кто не оплатил",
      "История оплат по ученику",
      "Меньше ручных напоминаний в чате",
    ],
    Art: ArtDebtors,
  },
  {
    id: "revenue",
    title: "Выручка",
    bullets: [
      "Оплаты в одном журнале",
      "Наличные, перевод и карта",
      "Понятная картина за месяц",
    ],
    Art: ArtRevenue,
  },
  {
    id: "acquiring",
    title: "Онлайн-оплата",
    bullets: [
      "Свои ключи клуба",
      "Родитель платит в кабинете",
      "Абонемент активируется после оплаты",
    ],
    Art: ArtAcquiring,
  },
];

const STEPS = [
  {
    title: "Создайте типы абонементов",
    text: "8 занятий, месяц, пробный — как привыкли вести секцию. Без программиста.",
  },
  {
    title: "Выдайте абонементы ученикам",
    text: "Вручную или после оплаты. Остаток виден вам и родителю.",
  },
  {
    title: "Отмечайте занятия",
    text: "Списание идёт само. Должники и выручка — в том же кабинете.",
  },
];

const FAQ = [
  {
    q: "Какие типы абонементов можно завести?",
    a: "По числу занятий и по сроку. Можно комбинировать: пробный, пакет на 8/12 занятий, месячный. Заморозку и продление ведёте в кабинете.",
  },
  {
    q: "Как происходит списание занятий?",
    a: "После отметки в журнале занятие списывается автоматически. Остаток сразу виден вам и родителю в кабинете секции.",
  },
  {
    q: "Как увидеть, кто не оплатил?",
    a: "Должники собраны в одном списке: кто должен, сколько и по какому абонементу. Не нужно сверять переписки и таблицы.",
  },
  {
    q: "Можно ли принимать оплату картой?",
    a: "Да. Подключаете свои ключи эквайринга — деньги идут на счёт клуба. Родитель оплачивает в кабинете, абонемент активируется после оплаты.",
  },
  {
    q: "Сколько это стоит и нужен ли программист?",
    a: `Первые ${TRIAL_DAYS} дней бесплатно, карта не нужна. Потом — от ${LANDING_PRICE_FROM} ₽ в месяц. Всё работает в браузере телефона, разработчик не нужен.`,
  },
];

export default function MembershipsLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    yandexMetrikaParams({ landing: "memberships" });
  }, []);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  const featuredCode = useMemo(() => {
    const withBadge = plans.find((p) => p.badge);
    return withBadge?.code || plans[1]?.code || plans[0]?.code;
  }, [plans]);

  const onTry = () => trackTryClick({ landing: "memberships" });

  return (
    <div className={s.page}>
      <Seo
        title={MEMBERSHIPS_LANDING_PAGE_TITLE}
        description={MEMBERSHIPS_LANDING_PAGE_DESCRIPTION}
        keywords={MEMBERSHIPS_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${MEMBERSHIPS_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildMembershipsLandingJsonLd(FAQ)}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <a
            href={buildClinicPortalUrl(tenant.subdomain, getDashboardPath(role))}
            className={`${s.btn} ${s.btnPrimary}`}
          >
            Открыть кабинет
          </a>
        </div>
      ) : null}

      <header className={s.nav}>
        <Link to="/" className={s.navBrand}>
          <img src={logo} alt="" className={s.navLogo} />
          <span>{L.productName}</span>
        </Link>
        <div className={s.navActions}>
          <a href="#capabilities" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Возможности
          </a>
          <a href="#pricing" className={`${s.btn} ${s.btnGhost} ${s.navLink}`}>
            Тарифы
          </a>
          <Link to="/account/login" className={`${s.btn} ${s.btnGhost}`}>
            Войти
          </Link>
          <Link
            to="/registraciya"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            Попробовать
          </Link>
        </div>
      </header>

      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroCopy}>
            <p className={s.heroEyebrow}>
              Абонементы и оплаты · {TRIAL_DAYS} дней бесплатно
            </p>
            <h1 className={s.heroTitle}>
              Абонементы, должники
              <br />
              и оплаты в одном месте
            </h1>
            <p className={s.heroLead}>
              Типы абонементов, выручка, кто не оплатил. Списание занятий после
              отметки. Онлайн-оплата картой — своими ключами клуба.
            </p>
            <div className={s.heroActions}>
              <Link
                to="/registraciya"
                className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
                onClick={onTry}
              >
                Попробовать {TRIAL_DAYS} дней бесплатно
              </Link>
              <a href="#capabilities" className={`${s.btn} ${s.btnSecondary} ${s.btnLg}`}>
                Смотреть возможности
              </a>
            </div>
            <p className={s.heroNote}>
              Карту указывать не нужно · от {LANDING_PRICE_FROM} ₽ после пробного
              периода
            </p>
          </div>
          <div className={s.heroVisual}>
            <ArtMembership />
          </div>
        </div>

        <div className={s.benefitRow}>
          {BENEFITS.map((item) => (
            <article key={item.title} className={s.benefitCard}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <span className={s.benefitIcon}>{item.icon}</span>
            </article>
          ))}
        </div>
      </section>

      <section id="capabilities" className={s.section}>
        <h2 className={s.sectionTitle}>Что закрывает учёт абонементов</h2>
        <p className={s.sectionLead}>
          Не отдельная таблица — абонементы связаны с журналом, выручкой и
          кабинетом родителей.
        </p>
        <div className={s.capGrid}>
          {CAPABILITIES.map((cap) => (
            <article key={cap.id} className={s.capCard}>
              <div className={s.capArt}>
                <cap.Art />
              </div>
              <h3>{cap.title}</h3>
              <ul>
                {cap.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className={s.section}>
        <div className={s.promoGrid}>
          <article className={s.promoCard}>
            <div className={s.promoCopy}>
              <h3>Кто не оплатил — сразу видно</h3>
              <p>
                Должники и остатки пакетов в одном кабинете. Меньше ручных
                напоминаний в WhatsApp.
              </p>
              <Link
                to="/registraciya"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={onTry}
              >
                Начать бесплатно
              </Link>
            </div>
            <ArtDebtors />
          </article>
          <article className={s.promoCard}>
            <div className={s.promoCopy}>
              <h3>Оплата картой на ваши ключи</h3>
              <p>
                Родитель платит в кабинете секции. Деньги идут клубу — без
                посредника-разработчика.
              </p>
              <a href="#pricing" className={`${s.btn} ${s.btnPrimary}`}>
                Смотреть тарифы
              </a>
            </div>
            <ArtAcquiring />
          </article>
        </div>
      </section>

      <section className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>
          Как начать
        </h2>
        <p className={`${s.sectionLead} ${s.sectionLeadCenter}`}>
          Три шага — и можно вести оплаты ближайшего месяца без Excel.
        </p>
        <div className={s.stepsTrack}>
          {STEPS.map((step, i) => (
            <div key={step.title} className={s.stepCol}>
              <div className={s.stepNumWrap}>
                <span className={s.stepNum}>{i + 1}</span>
                {i < STEPS.length - 1 ? <span className={s.stepLine} /> : null}
              </div>
              <article className={s.stepCard}>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className={s.section}>
        <h2 className={s.sectionTitle}>Тарифы</h2>
        <p className={s.sectionLead}>
          Абонементы есть на каждом тарифе. Первые {TRIAL_DAYS} дней — полный
          доступ бесплатно. Карта не нужна.
        </p>
        {plansLoading ? (
          <Preloader variant="inline" label="Загрузка тарифов…" />
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardFeatured : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePos}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small>/ мес</small>
                </p>
                <ul className={s.priceList}>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/registraciya"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnPrimary : s.btnSecondary
                  }`}
                  onClick={onTry}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
        <p className={s.sectionLead} style={{ marginTop: 16 }}>
          Подробнее о ценах — на странице{" "}
          <Link to="/tarify">тарифов</Link>.
        </p>
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Частые вопросы</h2>
        <p className={s.sectionLead}>
          Про типы абонементов, должников, онлайн-оплату и запуск без Excel.
        </p>
        <div className={s.faqList}>
          {FAQ.map((item, index) => (
            <details key={item.q} className={s.faqItem} open={index === 0}>
              <summary>
                <span>{item.q}</span>
                <span className={s.faqChevron} aria-hidden />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={s.ctaBand}>
        <h2>Возьмите оплаты под контроль</h2>
        <p>
          {TRIAL_DAYS} дней бесплатно. Потом — от {LANDING_PRICE_FROM} ₽ в месяц.
          Карту указывать не нужно.
        </p>
        <Link
          to="/registraciya"
          className={`${s.btn} ${s.btnPrimary} ${s.btnLg}`}
          onClick={onTry}
        >
          Попробовать бесплатно
        </Link>
      </section>

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>

      {!hasClub ? (
        <div className={s.sticky}>
          <Link
            to="/registraciya"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={onTry}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}
    </div>
  );
}
